
import { Dialog, DialogContent, InputAdornment, MenuItem, TextField } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import ComboBox from "components/ComboBox"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { initTransactionDialogData } from "./initData"
import { sparePartApiNm, sparePartTransactionColNm } from "./constant"
import { alertConfirmDelete } from "components/Alert"
import { bahtProps } from "utils/bahtProps"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import FilePreviewCard from "components/FileUplaod/FilePreviewCard"
import { selectOnFocus } from "utils"

const SparePartTransactionDialog = ({ selectedId, dialogOpen, setDialogOpen, onFinish, lastFilter }) => {

  const { ax, msData } = useContext(GlobalStateContext)
  const [dialogData, setDialogData] = useState({ ...initTransactionDialogData })
  const [origData, setOrigData] = useState({ ...initTransactionDialogData })
  const [filesData, setFilesData] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])
  const [selectedSparePart, setSelectedSparePart] = useState(null)
  const [sparePartCombo, setSparePartCombo] = useState([])

  const dialogDataProp = useCallback((name) => ({
    fullWidth: true,
    size: "small",
    label: sparePartTransactionColNm[name],
    value: dialogData[name] ?? "",
    onChange: (e) => setDialogData(o => ({ ...o, [name]: e.target.value })),
  }), [dialogData])

  const uploadFiles = useCallback(async (spTrnsId) => {
    for (const pf of pendingFiles) {
      const formData = new FormData()
      formData.append('file', pf.file)
      formData.append('SpTrnsId', spTrnsId)
      formData.append('Label', pf.name)
      await ax.post(sparePartApiNm.uploadSparePartTransactionFile, formData)
    }
    setPendingFiles([])
  }, [ax, pendingFiles])

  const handleDialogOk = useCallback(async () => {
    if (selectedId) {
      // Update: send only changed columns
      const colValues = {}
      for (const key of ['SpId', 'TrnsTyp', 'TrnsDte', 'Qty', 'UntPr', 'TukId', 'TlrId', 'SupId', 'DocNo', 'DocDte', 'Dscp', 'Rmk']) {
        if (String(dialogData[key] ?? '') !== String(origData[key] ?? '')) {
          colValues[key] = dialogData[key]
        }
      }
      const hasChange = Object.keys(colValues).length > 0
      const hasNewFiles = pendingFiles.length > 0
      if (hasChange) {
        await ax.post(sparePartApiNm.updateSparePartTransactionColumn, {
          SpTrnsId: selectedId,
          ColValues: colValues,
        })
      }
      if (hasNewFiles) {
        await uploadFiles(selectedId)
      }
      if (hasChange || hasNewFiles) {
        onFinish()
      }
      setDialogOpen(false)
    } else {
      // Insert - don't pass getArgs so we get insertId back
      const postData = { ...dialogData }
      const value = await ax.post(sparePartApiNm.insertSparePartTransaction, postData)
      if (value.data) {
        const spTrnsId = value.data
        if (spTrnsId && pendingFiles.length > 0) {
          await uploadFiles(spTrnsId)
        }
        onFinish()
        setDialogOpen(false)
      }
    }
  }, [ax, dialogData, origData, onFinish, setDialogOpen, selectedId, pendingFiles, uploadFiles])

  const handleDelete = useCallback(() => {
    alertConfirmDelete(() => {
      ax.post(sparePartApiNm.deleteSparePartTransaction, { SpTrnsId: selectedId, getArgs: lastFilter }).then(value => {
        if (value.data) {
          onFinish(value.data)
          setDialogOpen(false)
        }
      })
    })
  }, [ax, onFinish, lastFilter, selectedId, setDialogOpen])

  const handleDeleteServerFile = useCallback((file) => {
    return new Promise((resolve) => {
      alertConfirmDelete(() => {
        ax.post(sparePartApiNm.deleteSparePartTransactionFile, { SpTrnsFileId: file.SpTrnsFileId }).then(value => {
          if (value.data) {
            resolve()
          }
        })
      })
    })
  }, [ax])

  useEffect(() => {
    if (dialogOpen) {
      ax.post(sparePartApiNm.getSparePart, { IsActv: 1 }).then(value => {
        if (value.data) {
          const combo = value.data.map(item => ({
            id: item.SpId,
            label: `${item.SpCd ? item.SpCd + ' - ' : ''}${item.SpNm}`,
            Unt: item.Unt,
            UntPr: item.UntPr,
            CurStk: item.CurStk,
          }))
          setSparePartCombo(combo)
          if (selectedId) {
            ax.post(sparePartApiNm.getSparePartTransactionDetail, { SpTrnsId: selectedId }).then(value => {
              if (value.data && value.data.length > 0) {
                setDialogData(value.data[0])
                setOrigData(value.data[0])
                setFilesData(value.data[0].Files || [])
                setSelectedSparePart(combo.find(o => o.id === value.data[0].SpId) || null)
              }
            })
          } else {
            setFilesData([])
            setSelectedSparePart(null)
          }
        }
      })
    } else {
      setDialogData({ ...initTransactionDialogData })
      setOrigData({ ...initTransactionDialogData })
      setFilesData([])
      setSelectedSparePart(null)
      setSparePartCombo([])
      setPendingFiles(o => {
        o.forEach(f => URL.revokeObjectURL(f.preview))
        return []
      })
    }
  }, [dialogOpen, ax, selectedId])

  // Auto-calculate TotalPr
  useEffect(() => {
    const total = (+dialogData.Qty || 0) * (+dialogData.UntPr || 0)
    setDialogData(o => {
      if (o.TotalPr === total) return o
      return { ...o, TotalPr: total }
    })
  }, [dialogData.Qty, dialogData.UntPr])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='xs'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="รายการรับ-เบิกอะไหล่"
        onDeleteClick={selectedId ? handleDelete : null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          <BoxFR sx={{ gap: 1 }}>
            <ComboBox
              sx={{ flex: 1 }}
              options={sparePartCombo}
              label="อะไหล่"
              selectedId={dialogData.SpId}
              setSelectedId={(id) => {
                const sp = sparePartCombo.find(o => o.id === id) || null
                setSelectedSparePart(sp)
                setDialogData(o => ({ ...o, SpId: id, UntPr: sp?.UntPr ?? o.UntPr }))
              }}
              required
            />
            <TextField
              size="small"
              label="คงเหลือ"
              value={selectedSparePart?.CurStk ?? ""}
              InputProps={{
                readOnly: true,
                ...(selectedSparePart?.Unt ? { endAdornment: <InputAdornment position="end">{selectedSparePart.Unt}</InputAdornment> } : {})
              }}
              sx={{ width: 120 }}
            />
          </BoxFR>
          <TextField {...dialogDataProp('TrnsTyp')} select required>
            <MenuItem value="I">รับเข้า</MenuItem>
            <MenuItem value="O">เบิกออก</MenuItem>
          </TextField>
          <DesktopDatePicker
            label={sparePartTransactionColNm.TrnsDte}
            inputFormat="DD/MM/YYYY"
            value={dialogData.TrnsDte}
            onChange={(v) => setDialogData(o => ({ ...o, TrnsDte: v ? dayjs(v).format("YYYY-MM-DD") : null }))}
            renderInput={(params) => <TextField size="small" fullWidth {...params} />}
          />
          <BoxFR sx={{ gap: 1 }}>
            <TextField {...dialogDataProp('Qty')} type="number" required onFocus={selectOnFocus}
              InputProps={selectedSparePart?.Unt ? {
                endAdornment: <InputAdornment position="end">{selectedSparePart.Unt}</InputAdornment>
              } : undefined}
            />
            <TextField {...dialogDataProp('UntPr')} type="number" {...bahtProps} onFocus={selectOnFocus} />
          </BoxFR>
          <TextField {...dialogDataProp('TotalPr')} type="number" {...bahtProps} disabled />
          {dialogData.TrnsTyp === 'O' ? (
            <BoxFR sx={{ gap: 1 }}>
              <ComboBox
                sx={{ flex: 1 }}
                options={msData.truckCombo || []}
                label={sparePartTransactionColNm.TukId}
                selectedId={dialogData.TukId}
                setSelectedId={(id) => setDialogData(o => ({ ...o, TukId: id, TlrId: id ? null : o.TlrId }))}
              />
              <ComboBox
                sx={{ flex: 1 }}
                options={msData.trailerCombo || []}
                label={sparePartTransactionColNm.TlrId}
                selectedId={dialogData.TlrId}
                setSelectedId={(id) => setDialogData(o => ({ ...o, TlrId: id, TukId: id ? null : o.TukId }))}
              />
            </BoxFR>)
            : (<ComboBox
              options={msData.supplierCombo || []}
              label={sparePartTransactionColNm.SupId}
              selectedId={dialogData.SupId}
              setSelectedId={(id) => setDialogData(o => ({ ...o, SupId: id }))}
            />
            )
          }
          <BoxFR sx={{ gap: 1 }}>
            <TextField {...dialogDataProp('DocNo')} />
            <DesktopDatePicker
              label={sparePartTransactionColNm.DocDte}
              inputFormat="DD/MM/YYYY"
              value={dialogData.DocDte}
              onChange={(v) => setDialogData(o => ({ ...o, DocDte: v ? dayjs(v).format("YYYY-MM-DD") : null }))}
              renderInput={(params) => <TextField size="small" fullWidth {...params} />}
            />
          </BoxFR>
          <TextField {...dialogDataProp('Dscp')} multiline rows={2} />
          <TextField {...dialogDataProp('Rmk')} />
          <FilePreviewCard
            filesData={filesData}
            setFilesData={setFilesData}
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
            onDeleteServerFile={handleDeleteServerFile}
            fileIdKey="SpTrnsFileId"
          />
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk} />
    </Dialog>
  )
}

export default React.memo(SparePartTransactionDialog)
